import React, { useMemo, useState } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useControllerRollforward } from '../../../hooks/analytics/useControllerReports.ts';
import { formatDate, RefreshMeta, StatusChip } from './shared.tsx';
import type { ControllerRollforwardRow } from '../../../request/analytics/controllerReports.ts';

/** Report #24 — Re-performance & Roll-forward Tracker. Table with a rollforward-only filter toggle. */
export const RollforwardTable: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerRollforward();
  const [rollforwardOnly, setRollforwardOnly] = useState(false);

  const rows = useMemo(() => {
    const all = controlIdFilter ? (data ?? []).filter((r) => controlIdFilter.has(r.control_id)) : data ?? [];
    return rollforwardOnly ? all.filter((r) => r.is_rollforward) : all;
  }, [data, rollforwardOnly, controlIdFilter]);

  const columns = useMemo<TableColumnDef<ControllerRollforwardRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">
            {row.original.control_id} — {row.original.title}
          </span>
        ),
        widthPercent: 32,
      },
      {
        id: 'run_number',
        columnLabel: 'Run #',
        header: () => 'Run #',
        cell: ({ row }) => row.original.run_number,
        widthPercent: 8,
      },
      {
        id: 'type',
        columnLabel: 'Type',
        header: () => 'Type',
        cell: ({ row }) =>
          row.original.is_rollforward ? (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
              Roll-forward
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-700">
              Re-run
            </span>
          ),
        widthPercent: 14,
      },
      {
        id: 'rerun_reason',
        columnLabel: 'Reason',
        header: () => 'Reason',
        cell: ({ row }) => row.original.rerun_reason ?? '—',
        widthPercent: 22,
      },
      {
        id: 'workflow_status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.workflow_status} />,
        widthPercent: 12,
      },
      {
        id: 'created_at',
        columnLabel: 'Created',
        header: () => 'Created',
        cell: ({ row }) => formatDate(row.original.created_at),
        widthPercent: 12,
      },
    ],
    []
  );

  return (
    <AppDataTable<ControllerRollforwardRow>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load roll-forward tracker.' : null}
      emptyMessage="No re-runs or roll-forwards recorded."
      paginated
      defaultPageSize={10}
      headerStart={
        <div className="flex w-full items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={rollforwardOnly}
              onChange={(e) => setRollforwardOnly(e.target.checked)}
            />
            Roll-forward only
          </label>
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default RollforwardTable;
